import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { toast } from 'sonner'
import {
  Briefcase,
  DollarSign,
  GraduationCap,
  Hammer,
  HardHat,
  Heart,
  Home,
  Hotel,
  Loader2,
  Megaphone,
  Monitor,
  Scale,
  Search,
  Share2,
  ShoppingBag,
  Sparkles,
  UserCheck,
  Users,
  Truck,
  Wrench,
} from 'lucide-react'
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/select'
import { fetchCompanies } from '@/api/auth'
import { ApiHttpError } from '@/api/auth'
import { updateCompanyInfo } from '@/api/onboarding'
import { defaultLocale } from '@/lib/locales'
import { normaliserSite } from '@/lib/siteWeb'
import { etape } from '@/lib/analytics/inscription'
import { lire as lireCodePartenaire } from '@/lib/codePartenaire'
import { useProfile } from '@/api/profile'
import { CadreAvant, CHAMP_SOMBRE, Erreur, Etiquette } from '@/pages/onboarding/AvantPaiement'

// Messages dans la langue de la page : ils étaient en français pour tous.
function creerSchema(t: TFunction) {
  return z.object({
    companyName: z.string().min(2, t('onboarding.step2.errors.companyNameTooShort')),
    industry: z.string().min(1, t('onboarding.step2.errors.industryRequired')),
    // Sans « https:// », l'adresse est complétée (normaliserSite), pas refusée.
    website: z
      .string()
      .optional()
      .refine((v) => !v?.trim() || normaliserSite(v) !== null, {
        message: t('onboarding.step2.errors.websiteInvalid'),
      }),
    discovery: z.string().min(1, t('onboarding.step2.errors.discoveryRequired')),
    discoveryDetail: z.string().max(255).optional(),
    // Never blocks: an unknown code is ignored by the server.
    partnerCode: z.string().max(40).optional(),
  })
}

type Form = z.infer<ReturnType<typeof creerSchema>>

/**
 * D'où vient le client, dit par lui. PostHog sait par quelle porte il est entré ;
 * lui seul sait QUI lui en a parlé. Le champ libre des trois dernières options
 * est le plus utile : un nom de personne, de groupe ou de logiciel.
 */
const DECOUVERTES = [
  { value: 'google', icon: Search },
  { value: 'ia', icon: Sparkles },
  { value: 'recommandation', icon: UserCheck },
  { value: 'reseaux', icon: Share2 },
  { value: 'communaute', icon: Users },
  { value: 'publicite', icon: Megaphone },
  { value: 'logiciel', icon: Monitor },
  { value: 'autre', icon: Wrench },
] as const

/** Les choix qui méritent une précision : qui, où, lequel. */
const DECOUVERTES_AVEC_DETAIL = ['recommandation', 'communaute', 'logiciel', 'autre']

const INDUSTRIES = [
  { value: 'technology', icon: Monitor },
  { value: 'marketing', icon: Megaphone },
  { value: 'healthcare', icon: Heart },
  { value: 'realestate', icon: Home },
  { value: 'finance', icon: DollarSign },
  { value: 'retail', icon: ShoppingBag },
  { value: 'education', icon: GraduationCap },
  { value: 'construction', icon: HardHat },
  { value: 'legal', icon: Scale },
  { value: 'hospitality', icon: Hotel },
  { value: 'consulting', icon: UserCheck },
  { value: 'transport_logistics', icon: Truck },
  { value: 'trades', icon: Hammer },
  { value: 'other', icon: Wrench },
] as const

/** Fournisseurs grand public : leur domaine n'est pas le site de l'entreprise. */
const DOMAINES_PERSONNELS = new Set([
  'gmail.com',
  'googlemail.com',
  'hotmail.com',
  'hotmail.ca',
  'hotmail.fr',
  'outlook.com',
  'outlook.fr',
  'live.com',
  'live.ca',
  'live.fr',
  'msn.com',
  'yahoo.com',
  'yahoo.ca',
  'yahoo.fr',
  'icloud.com',
  'me.com',
  'mac.com',
  'aol.com',
  'proton.me',
  'protonmail.com',
  'videotron.ca',
  'videotron.qc.ca',
  'bell.net',
  'sympatico.ca',
  'rogers.com',
  'shaw.ca',
  'telus.net',
  'cogeco.ca',
  'globetrotter.net',
  'gmx.com',
  'gmx.fr',
  'orange.fr',
  'free.fr',
  'sfr.fr',
  'laposte.net',
  'mail.com',
  'zoho.com',
  'yandex.com',
])

/** Le domaine du courriel s'il est celui de l'entreprise (contact@workless.ca → workless.ca), sinon null. */
function domaineProfessionnel(email: string | undefined): string | null {
  const domaine = email?.split('@')[1]?.trim().toLowerCase()
  if (!domaine || !domaine.includes('.') || DOMAINES_PERSONNELS.has(domaine)) return null
  return domaine
}

function detectTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Toronto'
  } catch {
    return 'America/Toronto'
  }
}

export function OnboardingStep2Page() {
  // Repere : cet ecran de l'onboarding a ete atteint.
  useEffect(() => {
    etape('onboarding_etape2')
  }, [])

  const { locale = defaultLocale } = useParams<{ locale: string }>()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [timezone] = useState(detectTimezone)
  const { data: profil } = useProfile()
  const sitePro = domaineProfessionnel(profil?.email)

  // Guard : doit avoir un preferred_phone_number (step1 fait)
  const guardQuery = useQuery({
    queryKey: ['onboarding-guard-step2'],
    queryFn: fetchCompanies,
    retry: false,
  })

  useEffect(() => {
    if (!guardQuery.data) return
    const company = guardQuery.data.companies?.[0]
    if (!company?.preferred_phone_number) {
      navigate(`/${locale}/onboarding/step1`, { replace: true })
    }
  }, [guardQuery.data, navigate, locale])

  const schema = useMemo(() => creerSchema(t), [t])
  // A partner's link: « Recommandation » is already the answer and the code already says who.
  const [codeDuLien] = useState(() => lireCodePartenaire())
  const {
    register,
    handleSubmit,
    setValue,
    trigger,
    watch,
    formState: { errors },
  } = useForm<Form>({
    resolver: zodResolver(schema),
    // The code of a partner's link is already shown: the server tied the account to it at sign-up.
    defaultValues: {
      companyName: '',
      industry: '',
      website: '',
      discovery: codeDuLien ? 'recommandation' : '',
      discoveryDetail: '',
      partnerCode: codeDuLien,
    },
  })

  const industry = watch('industry')
  const discovery = watch('discovery')
  // The code field only makes sense for a recommendation; with the link's code, « Who told you? » is already answered.
  const recommande = discovery === 'recommandation'
  const avecDetail = DECOUVERTES_AVEC_DETAIL.includes(discovery) && !(recommande && codeDuLien)

  const submitMutation = useMutation({
    mutationFn: async (values: Form) => {
      await updateCompanyInfo({
        name: values.companyName.trim(),
        timezone,
        industry: values.industry,
        website: sitePro ? `https://${sitePro}` : normaliserSite(values.website ?? ''),
        discovery_source: values.discovery,
        discovery_detail: values.discoveryDetail?.trim() || '',
        partner_code: (values.discovery === 'recommandation' ? values.partnerCode?.trim() : codeDuLien) || '',
      })
    },
    onSuccess: () => navigate(`/${locale}/subscribe`, { replace: true }),
    onError: (error) => {
      if (error instanceof ApiHttpError) {
        const data = error.data as { error?: string; detail?: string }
        toast.error(data?.error || data?.detail || 'Erreur lors de la sauvegarde')
        return
      }
      console.error('[Step2]', error)
      toast.error(t('toasts.common.saveError'))
    },
  })

  // Le formulaire en deux écrans : l'entreprise, puis l'activité.
  const [ecran, setEcran] = useState<'entreprise' | 'activite'>('entreprise')

  const continuer = async () => {
    if (ecran === 'entreprise') {
      if (await trigger(['companyName', 'website'])) setEcran('activite')
      return
    }
    await handleSubmit((values) => submitMutation.mutate(values))()
  }
  const isSaving = submitMutation.isPending

  if (guardQuery.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A192F]">
        <Loader2 className="h-8 w-8 animate-spin text-white" />
      </div>
    )
  }

  return (
    <CadreAvant
      titre={
        ecran === 'entreprise' ? t('onboarding.step2.v2.nomQuestion') : t('onboarding.step2.v2.dernieresQuestions')
      }
      onRetour={() => (ecran === 'activite' ? setEcran('entreprise') : navigate(`/${locale}/onboarding/step1`))}
      onContinuer={() => void continuer()}
      enCours={isSaving}
    >
      {ecran === 'entreprise' ? (
        <div className="space-y-6">
          <div>
            <label htmlFor="company-name" className="sr-only">
              {t('onboarding.step2.companyNameLabel')}
            </label>
            <input
              id="company-name"
              type="text"
              autoFocus
              autoComplete="organization"
              placeholder={t('onboarding.step2.companyNamePlaceholder')}
              aria-invalid={!!errors.companyName}
              disabled={isSaving}
              className={CHAMP_SOMBRE}
              {...register('companyName')}
            />
            {errors.companyName && <Erreur>{errors.companyName.message}</Erreur>}
          </div>

          {/* Adresse pro : le site se déduit du courriel, on ne le demande pas. */}
          {!sitePro && (
            <div>
              <Etiquette htmlFor="website">
                {t('onboarding.step2.websiteLabel')}{' '}
                <span style={{ color: '#64748B' }}>{t('onboarding.step2.websiteOptional')}</span>
              </Etiquette>
              <input
                id="website"
                type="url"
                placeholder={t('onboarding.step2.websitePlaceholder')}
                disabled={isSaving}
                className={CHAMP_SOMBRE}
                {...register('website')}
              />
              {errors.website && <Erreur>{errors.website.message}</Erreur>}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          <div>
            <Etiquette>{t('onboarding.step2.v2.secteurLabel')}</Etiquette>
            <ListeSombre
              value={industry}
              onChange={(v) => setValue('industry', v, { shouldValidate: true })}
              placeholder={t('onboarding.step2.industryPlaceholder')}
              iconeVide={Briefcase}
              options={INDUSTRIES.map(({ value, icon }) => ({
                value,
                icon,
                label: t(`onboarding.step2.industries.${value}`),
              }))}
              disabled={isSaving}
            />
            {errors.industry && <Erreur>{errors.industry.message}</Erreur>}
          </div>

          <div>
            <Etiquette>{t('onboarding.step2.discoveryLabel')}</Etiquette>
            <ListeSombre
              value={discovery}
              onChange={(v) => {
                setValue('discovery', v, { shouldValidate: true })
                // Changer d'option vide la précision : elle ne veut plus rien dire.
                setValue('discoveryDetail', '')
              }}
              placeholder={t('onboarding.step2.discoveryPlaceholder')}
              iconeVide={Search}
              options={DECOUVERTES.map(({ value, icon }) => ({
                value,
                icon,
                label: t(`onboarding.step2.discoveries.${value}`),
              }))}
              disabled={isSaving}
            />
            {errors.discovery && <Erreur>{errors.discovery.message}</Erreur>}
            {avecDetail && (
              <input
                className={`${CHAMP_SOMBRE} mt-3`}
                maxLength={255}
                disabled={isSaving}
                placeholder={t(`onboarding.step2.discoveryDetails.${discovery}`)}
                {...register('discoveryDetail')}
              />
            )}
          </div>

          {recommande && (
            <div>
              <Etiquette htmlFor="partner-code">
                {t('onboarding.step2.partnerCodeLabel')}{' '}
                <span style={{ color: '#64748B' }}>{t('onboarding.step2.partnerCodeOptional')}</span>
              </Etiquette>
              <input
                id="partner-code"
                type="text"
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                maxLength={40}
                placeholder={t('onboarding.step2.partnerCodePlaceholder')}
                disabled={isSaving}
                className={CHAMP_SOMBRE}
                {...register('partnerCode')}
              />
            </div>
          )}
        </div>
      )}
    </CadreAvant>
  )
}

type Icone = typeof Search

/** Liste déroulante sur fond bleu nuit, pleine largeur comme les autres champs. */
function ListeSombre({
  value,
  onChange,
  placeholder,
  iconeVide,
  options,
  disabled,
}: {
  value: string
  onChange: (v: string) => void
  placeholder: string
  iconeVide: Icone
  options: { value: string; label: string; icon: Icone }[]
  disabled?: boolean
}) {
  const choisie = options.find((o) => o.value === value)
  const Icon = choisie?.icon || iconeVide
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className="w-full !h-12 px-4 rounded-xl text-[15px] cursor-pointer border-white/[0.14] bg-white/[0.04] dark:bg-white/[0.04] text-white hover:border-white/30 [&>svg]:!opacity-100 [&>svg]:!text-white/70">
        <span className="flex items-center gap-3 min-w-0">
          <Icon className="w-4 h-4 flex-shrink-0 !text-white/50" />
          <span className={`truncate ${choisie ? '' : 'text-white/35'}`}>{choisie?.label ?? placeholder}</span>
        </span>
      </SelectTrigger>
      <SelectContent className="bg-[#12223D] border-white/10 text-white">
        {options.map(({ value: v, label, icon: I }) => (
          <SelectItem key={v} value={v} className="focus:bg-white/10 focus:text-white">
            <span className="flex items-center gap-3">
              <I className="w-4 h-4 !text-white/60" />
              {label}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
